import React, { useMemo, useRef, useEffect, useState } from 'react';
import EvaluationModal from './EvaluationModal';

const ResultsSection = ({ answers, questions, competencies, onRestart }) => {
  const resultsRef = useRef(null);
  const [showEvalModal, setShowEvalModal] = useState(false);

  // Calculate competency scores
  const competencyScores = useMemo(() => {
    const scores = {};
    const competencyQuestions = {};

    // Group questions by competency
    questions.forEach(question => {
      if (!competencyQuestions[question.competency]) {
        competencyQuestions[question.competency] = [];
      }
      competencyQuestions[question.competency].push(question.id);
    });

    // Calculate average score for each competency
    Object.keys(competencies).forEach(competencyKey => {
      const questionIds = competencyQuestions[competencyKey] || [];
      if (questionIds.length === 0) return;

      const scores_array = questionIds.map(id => answers[id] || 0);
      const average = scores_array.reduce((sum, score) => sum + score, 0) / scores_array.length;

      scores[competencyKey] = {
        name: competencies[competencyKey].name,
        score: Math.round(average * 10) / 10,
        section: competencies[competencyKey].section,
        category: getScoreCategory(average)
      };
    });

    return scores;
  }, [answers, questions, competencies]);

  // Calculate section averages
  const sectionAverages = useMemo(() => {
    const sections = {
      'Önmagunk vezetése': ['onismeret', 'onuralom', 'integritas', 'tanulasi_agilitas', 'reziliencia'],
      'Mások vezetése': ['kommunikacio', 'empatia', 'motivacio', 'delegals', 'konfliktuskezeles', 'coaching'],
      'Szervezet vezetése': ['strategiai_gondolkodas', 'valtozasmenedzsment', 'dontes', 'innovacio', 'egyuttmukodes']
    };

    const averages = {};

    Object.keys(sections).forEach(sectionName => {
      const competencyKeys = sections[sectionName];
      const sectionScores = competencyKeys
        .filter(key => competencyScores[key])
        .map(key => competencyScores[key].score);

      if (sectionScores.length > 0) {
        const average = sectionScores.reduce((sum, score) => sum + score, 0) / sectionScores.length;
        averages[sectionName] = {
          name: sectionName,
          score: Math.round(average * 10) / 10,
          category: getScoreCategory(average)
        };
      }
    });

    return averages;
  }, [competencyScores]);

  // Generate development recommendations
  const recommendations = useMemo(() => {
    const needsAttention = [];
    const strengths = [];

    Object.keys(competencyScores).forEach(key => {
      const competency = competencyScores[key];
      if (competency.category === 'needs-attention') {
        needsAttention.push(competency);
      } else if (competency.category === 'strength') {
        strengths.push(competency);
      }
    });

    return { needsAttention, strengths };
  }, [competencyScores]);

  // Trigger height update when results are displayed
  useEffect(() => {
    const isInIframe = window.self !== window.top;

    if (isInIframe) {
      const timer = setTimeout(() => {
        const height = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );

        try {
          window.parent.postMessage({
            type: 'height',
            height: height,
            source: 'leadership-test'
          }, '*');
        } catch (error) {
          console.warn('Could not send height update to parent:', error);
        }
      }, 500);

      let resizeObserver;
      if (typeof ResizeObserver !== 'undefined' && resultsRef.current) {
        resizeObserver = new ResizeObserver(() => {
          const height = Math.max(
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight
          );

          try {
            window.parent.postMessage({
              type: 'height',
              height: height,
              source: 'leadership-test'
            }, '*');
          } catch (error) {
            console.warn('Could not send height update to parent:', error);
          }
        });

        resizeObserver.observe(resultsRef.current);
      }

      return () => {
        clearTimeout(timer);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [answers, questions, competencies]);

  return (
    <div className="results-section" ref={resultsRef}>
      <h2>Az Ön Vezetői Kompetencia Profilja</h2>

      {/* Section Summaries */}
      <div className="section-summaries">
        {Object.keys(sectionAverages).map(sectionName => {
          const sectionData = sectionAverages[sectionName];
          return (
            <div key={sectionName} className="section-summary">
              <h3>{sectionData.name}</h3>
              <span className="section-average">{sectionData.score}</span>
              <p>{getSectionInterpretation(sectionData.score)}</p>
            </div>
          );
        })}
      </div>

      {/* Detailed Competency Results */}
      <div className="detailed-results">
        <h3 style={{ textAlign: 'center', margin: '2rem 0', color: 'var(--gray-800)' }}>
          Részletes Kompetencia Eredmények
        </h3>

        {Object.keys(sectionAverages).map(sectionName => {
          const sectionCompetencies = Object.keys(competencyScores)
            .filter(key => competencyScores[key].section === sectionName)
            .map(key => ({ key, ...competencyScores[key] }));

          return (
            <div key={sectionName} className="section-results">
              <h4 style={{
                color: 'var(--primary-green)',
                margin: '1.5rem 0 1rem',
                fontSize: '1.2rem'
              }}>
                {sectionName}
              </h4>

              {sectionCompetencies.map(competency => (
                <div key={competency.key} className={`competency-result ${competency.category}`}>
                  <div className="competency-name">{competency.name}</div>
                  <div className="competency-score">{competency.score}/5</div>
                  <div className="competency-interpretation">
                    {getCompetencyInterpretation(competency.score)}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Development Recommendations */}
      <div className="development-recommendations">
        <h3 style={{ textAlign: 'center', margin: '2rem 0', color: 'var(--gray-800)' }}>
          Fejlesztési Javaslatok
        </h3>

        {recommendations.needsAttention.length > 0 && (
          <div className="needs-attention-section">
            <h4 style={{ color: '#ef4444', marginBottom: '1rem' }}>
              🎯 Fejlesztendő Területek (≤3.0 pont)
            </h4>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
              Ezek a kompetenciák azonnali figyelmet és fejlesztést igényelnek:
            </p>
            {recommendations.needsAttention.map(competency => (
              <div key={competency.name} className="recommendation-item">
                <strong>{competency.name}</strong> - {competency.score}/5
              </div>
            ))}
          </div>
        )}

        {recommendations.strengths.length > 0 && (
          <div className="strengths-section">
            <h4 style={{ color: 'var(--primary-green)', margin: '2rem 0 1rem' }}>
              💪 Az Ön Erősségei (≥4.0 pont)
            </h4>
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
              Ezekre a kompetenciákra bátran támaszkodhat:
            </p>
            {recommendations.strengths.map(competency => (
              <div key={competency.name} className="recommendation-item">
                <strong>{competency.name}</strong> - {competency.score}/5
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        <button
          type="button"
          className="pdf-export-btn"
          onClick={() => setShowEvalModal(true)}
          style={{
            backgroundColor: '#156B5C',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          Kérem a teszt kiértékelését
        </button>
        <button
          type="button"
          className="restart-btn"
          onClick={onRestart}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            padding: '12px 24px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          🔄 Új Teszt Kitöltése
        </button>
      </div>

      {/* Evaluation Modal */}
      <EvaluationModal
        isOpen={showEvalModal}
        onClose={() => setShowEvalModal(false)}
        resultsRef={resultsRef}
        competencyScores={competencyScores}
        sectionAverages={sectionAverages}
        answers={answers}
        questions={questions}
      />
    </div>
  );
};

// Helper functions
const getScoreCategory = (score) => {
  if (score >= 4) return 'strength';
  if (score <= 3) return 'needs-attention';
  return 'average';
};

const getSectionInterpretation = (score) => {
  if (score >= 4.5) return 'Kiváló! Ez egy nagyon erős területe.';
  if (score >= 4.0) return 'Erős terület. Bátran támaszkodhat ezekre a képességeire.';
  if (score >= 3.5) return 'Átlag feletti szint. Kisebb fejlesztésekkel jelentősen javíthat.';
  if (score >= 3.0) return 'Átlagos szint. Érdemes lenne ezen a területen fejlődni.';
  if (score >= 2.5) return 'Fejlesztésre szorul. Koncentráljon erre a területre.';
  return 'Azonnali figyelmet igényel. Javasolt szakmai segítséget kérni.';
};

const getCompetencyInterpretation = (score) => {
  if (score >= 4.5) return '🌟 Kiváló szint - folytassa így!';
  if (score >= 4.0) return '✅ Erősség - továbbra is fejlessze!';
  if (score >= 3.5) return '📈 Jó szint - kisebb fejlesztés javasolt';
  if (score >= 3.0) return '⚠️ Átlagos - fejlesztésre szorul';
  if (score >= 2.5) return '🎯 Fejlesztendő terület';
  return '🚨 Azonnali figyelmet igényel';
};

export default ResultsSection;
